import { handleActions } from 'redux-actions';
import * as AT from 'redux/actionTypes';
import { Auth0Profile } from 'utils/auth/AuthService';
import { AUTH_SESSION_STATUS, AuthSessionStatus } from 'utils/auth/sessionTypes';

interface AuthState {
  profile?: Auth0Profile | null;
  scope?: string | null;
  accessToken?: string | null;
  issuedAt?: number | null;
  expiresAt?: number | null;
  generationId?: string | null;
  sessionStatus: AuthSessionStatus;
  authenticationReason?: string | null;
  triedAuthenticateFromLocalStorage: boolean;
  isLoggingOut: boolean;
  defaultLabId?: string | null;
}

interface AuthActionPayload {
  profile?: Auth0Profile | null;
  scope?: string | null;
  accessToken?: string | null;
  issuedAt?: number | null;
  expiresAt?: number | null;
  generationId?: string | null;
  sessionStatus?: AuthSessionStatus;
  authenticationReason?: string | null;
  reason?: string | null;
  auth0UserId?: string | null;
  userId?: string;
}

type AuthAction = { payload: AuthActionPayload };

const getAuth0UserId = (profile?: Auth0Profile | null) => profile?.auth0UserId || profile?.userId;

// Session snapshots carry the Auth0-built profile; keep the resolved internal user ID for the same subject.
const keepInternalUserId = (incoming: Auth0Profile | null | undefined, current: Auth0Profile | null | undefined) =>
  incoming &&
  current &&
  current.userId !== getAuth0UserId(current) &&
  getAuth0UserId(incoming) === getAuth0UserId(current)
    ? { ...incoming, auth0UserId: getAuth0UserId(incoming), userId: current.userId }
    : incoming;

const initialState: AuthState = {
  profile: null,
  scope: null,
  accessToken: null,
  issuedAt: null,
  expiresAt: null,
  generationId: null,
  sessionStatus: AUTH_SESSION_STATUS.bootstrapping,
  authenticationReason: null,
  triedAuthenticateFromLocalStorage: false,
  isLoggingOut: false,
  defaultLabId: null,
};

const authReducer = handleActions(
  {
    [AT.LOGIN_SUCCESS]: (state: AuthState, action: AuthAction) => ({
      ...state,
      profile: keepInternalUserId(action.payload.profile, state.profile),
      scope: action.payload.scope,
      accessToken: action.payload.accessToken,
      issuedAt: action.payload.issuedAt ?? state.issuedAt ?? null,
      expiresAt: action.payload.expiresAt ?? state.expiresAt ?? null,
      generationId: action.payload.generationId ?? state.generationId ?? null,
      sessionStatus: action.payload.sessionStatus ?? AUTH_SESSION_STATUS.authenticated,
      authenticationReason: action.payload.authenticationReason ?? null,
      triedAuthenticateFromLocalStorage: true,
      isLoggingOut: false,
      defaultLabId: action.payload.profile?.labId,
    }),
    [AT.RENEW_TOKEN]: (state: AuthState, action: AuthAction) => ({
      ...state,
      accessToken: action.payload.accessToken,
      scope: action.payload.scope,
      issuedAt: action.payload.issuedAt ?? state.issuedAt ?? null,
      expiresAt: action.payload.expiresAt ?? state.expiresAt ?? null,
      generationId: action.payload.generationId ?? state.generationId ?? null,
      sessionStatus: AUTH_SESSION_STATUS.authenticated,
      authenticationReason: null,
    }),
    [AT.AUTH_SESSION_SNAPSHOT]: (state: AuthState, action: AuthAction) => ({
      ...state,
      profile: keepInternalUserId(action.payload.profile, state.profile),
      scope: action.payload.scope,
      accessToken: action.payload.accessToken,
      issuedAt: action.payload.issuedAt ?? null,
      expiresAt: action.payload.expiresAt ?? null,
      generationId: action.payload.generationId ?? null,
      sessionStatus: action.payload.sessionStatus ?? state.sessionStatus,
      authenticationReason: action.payload.authenticationReason ?? null,
      triedAuthenticateFromLocalStorage:
        action.payload.sessionStatus === AUTH_SESSION_STATUS.bootstrapping
          ? state.triedAuthenticateFromLocalStorage
          : true,
      isLoggingOut: action.payload.sessionStatus === AUTH_SESSION_STATUS.loggingOut,
      defaultLabId: action.payload.profile?.labId ?? state.defaultLabId ?? null,
    }),
    [AT.AUTH_SESSION_DEGRADED]: (state: AuthState, action: AuthAction) => ({
      ...state,
      sessionStatus: AUTH_SESSION_STATUS.degraded,
      authenticationReason: action.payload.reason,
    }),
    [AT.AUTH_REAUTHENTICATION_REQUIRED]: (state: AuthState, action: AuthAction) => ({
      ...state,
      sessionStatus: AUTH_SESSION_STATUS.reauthenticationRequired,
      authenticationReason: action.payload.reason,
      isLoggingOut: false,
    }),
    [AT.AUTH_AUTHENTICATION_ERROR]: (state: AuthState, action: AuthAction) => ({
      ...state,
      sessionStatus: AUTH_SESSION_STATUS.authenticationError,
      authenticationReason: action.payload.reason,
      triedAuthenticateFromLocalStorage: true,
      isLoggingOut: false,
    }),
    [AT.FINISHED_TRY_AUTHENTICATE_FROM_LOCAL_STORAGE]: (state) => ({
      ...state,
      triedAuthenticateFromLocalStorage: true,
    }),
    [AT.AUTH_INTERNAL_USER_ID_RESOLVED]: (state: AuthState, action: AuthAction) =>
      state.profile && action.payload.userId && getAuth0UserId(state.profile) === action.payload.auth0UserId
        ? {
            ...state,
            profile: { ...state.profile, auth0UserId: action.payload.auth0UserId, userId: action.payload.userId },
          }
        : state,
    [AT.LOGOUT]: (state) => ({
      ...state,
      profile: null,
      scope: null,
      accessToken: null,
      issuedAt: null,
      expiresAt: null,
      generationId: null,
      sessionStatus: AUTH_SESSION_STATUS.loggingOut,
      authenticationReason: 'explicit_logout',
      triedAuthenticateFromLocalStorage: false,
      isLoggingOut: true,
      defaultLabId: null,
    }),
  },
  initialState
);

export default authReducer;
